import { z } from 'zod';
import { BaseErrorSchema } from '../../../../shared/error.schema';
import { UserSchema } from '../../../../entities/user/user.schema';
/**
 * List Tenant Users Request Schema
 * GET /api/v2/tenants/:tenantId/users
 *
 * Path parameters:
 * - tenantId: The tenant ID to retrieve users for
 *
 * Returns all non-deleted users within the specified tenant
 */ export const ListTenantUsersRequestSchema = z.object({
    tenantId: z.string()
});
/**
 * Admin list item — the base user enriched with the identity's home
 * organization (null when the identity is unpinned or has no org) and the
 * complete set of tenantIds it may be re-homed to. This is the full
 * valid-target set the home-org move would accept: sign-in-eligible
 * (active|pending) memberships whose tenant is SSO-enabled, and [] for a staff
 * or disabled identity that can never be re-homed. A consumer can offer these
 * directly — none is a target the move would reject. Endpoint-scoped on
 * purpose: the global UserSchema is shared by many responses that do not carry
 * this enrichment.
 *
 * homeOrgDeleted flags identities pinned to a soft-deleted tenant's org: the
 * account is (or will be) locked out of every tenant until a new home org is
 * assigned. In that case homeOrgTenantId stays null so consumers never offer
 * the deleted tenant as a move target.
 *
 * activationLinkAvailable answers the whole question the admin UI asks —
 * "may I offer 'copy activation link' for this user?" — so no consumer has to
 * recompose it: the user must still be awaiting activation (inactive|pending)
 * AND the registry identity must not carry a zitadelUserId (a linked identity
 * has signed in at least once, so its account provably holds credentials and
 * no invite code can be minted for it).
 *
 * The link half is a ONE-WAY signal: false proves credentials, true does NOT
 * prove their absence (an unlinked account can still have set a password
 * through an invite link). The backend's 409 covers what this flag cannot see.
 * Deliberately not a Zitadel-state sync.
 *
 * signInResetAvailable mirrors the reset-sign-in endpoint's eligibility rule
 * (active non-staff user, home org known, not deleted, not SSO-enforced). For
 * a linked identity with no stored home org, only the user half is judged:
 * the endpoint resolves that account's real home org and re-checks it there.
 */ export const TenantUserAdminListItemSchema = UserSchema.extend({
    homeOrgTenantId: z.string().nullable(),
    eligibleHomeOrgTenantIds: z.array(z.string()),
    homeOrgDeleted: z.boolean(),
    activationLinkAvailable: z.boolean(),
    signInResetAvailable: z.boolean()
});
/**
 * List Tenant Users Response Schema
 * Returned on successful retrieval
 * Contains all non-deleted users for the specified tenant
 */ export const ListTenantUsersResponseSchema = z.array(TenantUserAdminListItemSchema);
/**
 * List Tenant Users Error Schema
 * Returned when retrieval fails
 */ export const ListTenantUsersErrorSchema = BaseErrorSchema;

//# sourceMappingURL=list-tenant-users.schema.js.map